<template>
  <b-col class="h-100">
    <p style="font-weight: bold; font-size: 1.2rem;">Edit plot labels &amp; colorbar</p>
    <div>
      <div
        v-for="trace in traces"
        :key="`${chartId}-${trace.id}--modes`"
        class="pb-1"
      >
        <b-alert :show="trace.type == 'heatmap'" dismissible
          >
          <p>
            Marker type selection (line vs. point) is not available
            for datashaded plots. Datashading is an image
            representation technique used to display high-resolution
            data.
            <a
              href="https://cormorack.github.io/resources/visualization/Datashading.html"
              target="_blank"
              >Learn more here.</a
            >
          </p>
          <p>
            <i class="fas fa-lightbulb"></i> Tip: To produce a non-datashaded plot,
            please choose a shorter time range (~1 day).
          </p>
        </b-alert>
        <b-input-group
          class="mb-2"
          v-if="trace.type == 'scattergl'"
        >
          <b-input-group-prepend is-text>
            Marker type
          </b-input-group-prepend>
          <b-input-group-prepend is-text>
            <b-form-radio
              v-model="trace.mode"
              name="scatter-markers"
              value="markers"
              :disabled="!hasData"
              >Point</b-form-radio
            >
          </b-input-group-prepend>
          <b-input-group-append is-text>
            <b-form-radio
              v-model="trace.mode"
              name="lines"
              value="lines"
              :disabled="!hasData"
              >Line</b-form-radio
            >
          </b-input-group-append>
          <b-input-group-append is-text>
            <b-form-radio
              v-model="trace.mode"
              name="linesAndMarkers"
              value="lines+markers"
              :disabled="!hasData"
              >Point &amp; Line</b-form-radio
            >
          </b-input-group-append>
        </b-input-group>

        <b-input-group class="mb-3">
          <template #prepend>
            <b-input-group-text>Plot Title</b-input-group-text>
          </template>
          <b-form-input
            v-model="plotTitle"
            placeholder="Enter plot title"
            :disabled="!hasData"
            debounce="800"
          ></b-form-input>
        </b-input-group>

        <b-input-group class="mb-3">
          <template #prepend>
            <b-input-group-text
              >Parameter 1 (X-axis)</b-input-group-text
            >
          </template>
          <b-form-input
            v-model="xaxisTitle"
            placeholder="Enter x-axis title"
            :disabled="!hasData"
            debounce="800"
          ></b-form-input>
        </b-input-group>

        <b-input-group class="mb-3">
          <template #prepend>
            <b-input-group-text
              >Parameter 2 (Y-axis)</b-input-group-text
            >
          </template>
          <b-form-input
            v-model="yaxisTitle"
            placeholder="Enter y-axis title"
            :disabled="!hasData"
            debounce="800"
          ></b-form-input>
        </b-input-group>

        <!--Display if colorbar is active-->
        <div v-if="colorActive">
          <!--Datashaded colorbar-->
          <div v-if="currentTrace.type === 'heatmap'">
            <datashaded-options
              :chart="chart"
              :chartId="chartId"
              :hasData="hasData"
              :currentTrace="currentTrace"
            ></datashaded-options>
          </div>
          <!--Marker colorbar-->
          <div v-if="currentTrace.type === 'scattergl'">
            <marker-options
              :chart="chart"
              :chartId="chartId"
              :hasData="hasData"
              :currentTrace="currentTrace"
            ></marker-options>
          </div>
        </div>
      </div>
    </div>
  </b-col>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import colorbarDatashadedOptions from './colorbarDatashadedForm';
import colorbarMarkerOptions from './colorbarMarkerForm';

export default {
  props: ['chart', 'chartId', 'hasData'],
  data() {
    return {
      titles: {
        title: '',
        x: {
          text: '',
        },
        y: {
          text: '',
        },
        color: {
          text: '',
          active: false,
          value: null,
          colorbar: 'thermal',
          range: {
            min: null,
            max: null,
          },
        },
      },
    };
  },
  components: {
    'marker-options': colorbarMarkerOptions,
    'datashaded-options': colorbarDatashadedOptions,
  },
  methods: {
    ...mapActions('visualization/charts', ['updatePlotLayout']),
  },
  computed: {
    ...mapGetters('visualization/traces', {
      getTraces: 'findByUUID',
      getTrace: 'find',
      getInstruments: 'findSelectedInstruments',
      getSelectedAxis: 'getSelectedAxis',
      getDateTimeRange: 'getDateTimeRange',
    }),
    ...mapState('visualization/traces', {
      allTraces: (state) => state.byId,
    }),
    uuid() {
      return this.$route.params.id;
    },
    traces() {
      return this.getTraces(this.uuid);
    },
    currentTrace() {
      if (this.traces.length > 0) {
        return this.allTraces[this.$route.query.d];
      }
      return null;
    },
    id() {
      if (this.currentTrace) {
        return this.currentTrace.id;
      }
      return 1;
    },
    chartLayout() {
      if (this.traces.length === 0) {
        return this.titles;
      }
      return this.chart.layout;
    },
    colorActive() {
      return this.currentTrace.selectedAxis.color.active;
    },
    // Get/set for plot title
    // Update title automatically once user types in new input
    plotTitle: {
      get() {
        return this.chartLayout.title;
      },
      set(value) {
        const layout = { ...this.chartLayout };
        layout.title = value;
        this.updatePlotLayout({ uuid: this.uuid, layout });
      },
    },
    // Get/set for xaxis title
    // Update title automatically once user types in new input
    xaxisTitle: {
      get() {
        return this.chartLayout.xaxis.title.text;
      },
      set(value) {
        const layout = { ...this.chartLayout };
        layout.xaxis.title.text = value;
        this.updatePlotLayout({ uuid: this.uuid, layout });
      },
    },
    // Get/set for yaxis title
    // Update title automatically once user types in new input
    yaxisTitle: {
      get() {
        return this.chartLayout.yaxis.title.text;
      },
      set(value) {
        const layout = { ...this.chartLayout };
        layout.yaxis.title.text = value;
        this.updatePlotLayout({ uuid: this.uuid, layout });
      },
    },
  },
};
</script>
